// S11 — Datos de máquina por completar (supervisor).
//
// Lo que las líneas conectadas a máquina dejan pendiente en las plantas del
// supervisor: paros sin causal y horas sin lote/operarios. El supervisor es el
// respaldo del líder (contrato de ingesta §7): completa sin ventana de tiempo.
//
// Reusa el banner, el diálogo de causal y la card de la captura L2. Con "Ver
// como" activo se listan sin acciones (el API respondería 403).

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { ParoPendienteCausal } from 'schemas';
import { Spinner } from '../../components/ui/spinner';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SupervisorSidebar } from '../../components/layout/SupervisorSidebar';
import { ApiError } from '../../lib/api/client';
import {
  useCompletarCausalParo,
  useCompletarRegistro,
  useParosPendientesCausal,
  useRegistrosPendientesCompletar,
} from '../../lib/api/ingesta';
import { useCausalesParo } from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { useVerComoActivo } from '../../lib/ver-como';
import {
  BannerParosPendientesCausal,
  CompletarCausalDialog,
} from '../captura/BannerParosPendientesCausal';
import { PendientesCompletarCard } from '../captura/PendientesCompletarCard';

function mensajeError(err: unknown, fallback: string): string {
  return err instanceof ApiError || err instanceof Error ? err.message : fallback;
}

export function S11DatosMaquinaPage() {
  const { state } = useAuth();
  const soloLectura = useVerComoActivo();
  const parosQ = useParosPendientesCausal();
  const registrosQ = useRegistrosPendientesCompletar();
  const causalesQ = useCausalesParo();
  const completarCausal = useCompletarCausalParo();
  const completarRegistro = useCompletarRegistro();

  const [paroACompletar, setParoACompletar] = useState<ParoPendienteCausal | null>(null);
  const [errorCausal, setErrorCausal] = useState<string | null>(null);
  const [guardandoId, setGuardandoId] = useState<string | null>(null);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  const paros = parosQ.data ?? [];
  const registros = registrosQ.data ?? [];
  const cargando = parosQ.isLoading || registrosQ.isLoading;
  const error = parosQ.error ?? registrosQ.error;

  return (
    <RoleLayout sidebar={<SupervisorSidebar />} roleLabel="Supervisor">
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <header className="border-b border-border pb-3">
          <h1 className="text-xl font-medium text-fg">Datos de máquina por completar</h1>
          <p className="mt-1 text-[12px] text-fg-tertiary">
            La máquina registra cuánto y cuándo; aquí se completa lo que solo una persona sabe: la
            causal de cada paro y el lote y los operarios de cada hora.
          </p>
        </header>

        {cargando ? (
          <div className="flex items-center gap-2 py-10 text-sm text-fg-secondary">
            <Spinner className="size-4" /> Cargando…
          </div>
        ) : error ? (
          <p role="alert" className="rounded bg-crit-soft px-3 py-2 text-sm text-crit">
            {mensajeError(error, 'No pudimos cargar los datos de máquina')}
          </p>
        ) : paros.length === 0 && registros.length === 0 ? (
          <div className="rounded border border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm font-medium text-fg">Nada pendiente</p>
            <p className="mt-1 text-[12px] text-fg-tertiary">
              Todos los paros de máquina tienen causal y todas las horas están completas.
            </p>
          </div>
        ) : (
          <>
            {soloLectura ? (
              <p className="rounded border border-border bg-surface-overlay px-3 py-2 text-xs text-fg-secondary">
                Está en modo «Ver como»: puede consultar, pero no completar.
              </p>
            ) : null}
            <BannerParosPendientesCausal
              paros={paros}
              soloLectura={soloLectura}
              onCompletar={(p) => {
                setErrorCausal(null);
                setParoACompletar(p);
              }}
            />
            <PendientesCompletarCard
              registros={registros}
              soloLectura={soloLectura}
              guardandoId={guardandoId}
              mostrarLinea
              onConfirmar={(registroId, body) => {
                setGuardandoId(registroId);
                completarRegistro.mutate(
                  { registroId, body },
                  {
                    onSuccess: () => toast.success('Hora completada'),
                    onError: (err) =>
                      toast.error(mensajeError(err, 'No se pudo completar la hora')),
                    onSettled: () => setGuardandoId(null),
                  },
                );
              }}
            />
          </>
        )}
      </div>

      {paroACompletar ? (
        <CompletarCausalDialog
          paro={paroACompletar}
          causales={causalesQ.data ?? []}
          guardando={completarCausal.isPending}
          serverError={errorCausal}
          onClose={() => setParoACompletar(null)}
          onGuardar={(body) => {
            setErrorCausal(null);
            completarCausal.mutate(
              { paroId: paroACompletar.id, body },
              {
                onSuccess: () => {
                  toast.success('Causal asignada');
                  setParoACompletar(null);
                },
                onError: (err) => setErrorCausal(mensajeError(err, 'No se pudo asignar la causal')),
              },
            );
          }}
        />
      ) : null}
    </RoleLayout>
  );
}
